import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert, 
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Grid,
  Container
} from '@mui/material';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../contexts/AuthContext';
import { registerUser, requestVerification } from '../data/userApi';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [verificationCaptchaToken, setVerificationCaptchaToken] = useState<string | null>(null);
  const [showVerificationCaptcha, setShowVerificationCaptcha] = useState(false);
  
  const { isAuthenticated } = useAuth();

  // If already authenticated, redirect to home page
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (token) setCaptchaError(null);
  };

  const handleVerificationCaptchaChange = (token: string | null) => {
    setVerificationCaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setApiError(null);
    setCaptchaError(null);

    if (!validateForm()) {
      return;
    }

    if (!captchaToken) {
      setCaptchaError('Please complete the captcha.');
      return;
    }

    setLoading(true);
    try {
      await registerUser({
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName || undefined,
        username: formData.email,
        h_captcha_token: captchaToken,
      });
      setRegistrationSuccess(true);
      setRegisteredEmail(formData.email);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: ''
      });
      setCaptchaToken(null);
    } catch (err: any) {
      console.error('Registration error:', err);
      setApiError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;
    
    if (!verificationCaptchaToken) {
      setShowVerificationCaptcha(true);
      return;
    }
    
    setLoading(true);
    try {
      await requestVerification(registeredEmail, verificationCaptchaToken);
      setVerificationSent(true);
      setShowVerificationCaptcha(false);
      setVerificationCaptchaToken(null);
    } catch (err: any) {
      console.error('Verification request error:', err);
      setApiError(err.message || 'Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ p: 4 }}
        role="main"
        aria-labelledby="register-title"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <PersonAddIcon color="primary" sx={{ fontSize: 40, mb: 2 }} aria-hidden="true" />
          <Typography variant="h4" component="h1" id="register-title" gutterBottom>
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Join us to shop and track your orders
          </Typography>
        </Box>

        {apiError && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {apiError}
          </Alert>
        )}

        {registrationSuccess ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }} role="status">
              Registration successful! Please check your email to verify your account.
            </Alert>
            
            {verificationSent ? (
              <Alert severity="info" sx={{ mb: 3 }} role="status">
                Verification email has been resent. Please check your inbox.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" gutterBottom>
                  Didn't receive the verification email?
                </Typography>
                {showVerificationCaptcha ? (
                  <Box sx={{ my: 2 }}>
                    <HCaptcha
                      sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001'}
                      onVerify={handleVerificationCaptchaChange}
                      onError={(err) => console.error('hCaptcha Error:', err)}
                      aria-label="Verification captcha"
                    />
                  </Box>
                ) : null}
                <Button
                  onClick={handleResendVerification}
                  disabled={loading}
                  sx={{ mt: 2 }}
                  aria-label="Resend verification email"
                >
                  Resend Verification Email
                </Button>
              </>
            )}
          </Box>
        ) : (
          <form 
            onSubmit={handleSubmit}
            noValidate
            aria-describedby={apiError ? 'form-error' : undefined}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={loading}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  id="fullName"
                  autoComplete="name"
                  inputProps={{
                    'aria-describedby': errors.fullName ? 'fullName-error' : undefined,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={loading}
                  error={!!errors.email}
                  helperText={errors.email}
                  id="email"
                  autoComplete="email"
                  inputProps={{
                    'aria-describedby': errors.email ? 'email-error' : undefined,
                  }}
                  aria-required="true"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={loading}
                  error={!!errors.password}
                  helperText={errors.password}
                  id="password"
                  autoComplete="new-password"
                  inputProps={{
                    'aria-describedby': errors.password ? 'password-error' : undefined,
                  }}
                  aria-required="true"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={togglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff aria-hidden="true" /> : <Visibility aria-hidden="true" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={loading}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  id="confirmPassword"
                  autoComplete="new-password"
                  inputProps={{
                    'aria-describedby': errors.confirmPassword ? 'confirmPassword-error' : undefined,
                  }}
                  aria-required="true"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                          onClick={toggleConfirmPasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff aria-hidden="true" /> : <Visibility aria-hidden="true" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ my: 2 }}>
                  <HCaptcha
                    sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001'}
                    onVerify={handleCaptchaChange}
                    onError={(err) => console.error('hCaptcha Error:', err)}
                    aria-label="Registration captcha"
                  />
                  {captchaError && (
                    <Typography 
                      color="error" 
                      variant="caption" 
                      role="alert"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {captchaError}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
              aria-busy={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link 
                  component={RouterLink} 
                  to="/login"
                  underline="hover"
                  aria-label="Sign in to your existing account"
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default Register; 